<script lang="ts">
  import { Link, router } from "@inertiajs/svelte";
  import { untrack, onMount } from "svelte";
  import type { UserProfile, PublicTrip } from "../../lib/api";
  import { usersApi } from "../../lib/api";
  import { addNotification } from "../../lib/stores";
  import Layout from "../../components/layout/Layout.svelte";

  let { profile, user = null, followers = [], following = [], publicTrips = [] }: {
    profile: UserProfile;
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
    followers?: { id: string; name: string; nickname: string; avatar_url: string | null }[];
    following?: { id: string; name: string; nickname: string; avatar_url: string | null }[];
    publicTrips?: PublicTrip[];
  } = $props();

  let isFollowing = $state(untrack(() => profile.is_following));
  let followersCount = $state(untrack(() => profile.stats.followers_count));
  let loading = $state(false);
  let activeTab = $state<'trips' | 'followers' | 'following'>('trips');
  let trips = $state<PublicTrip[]>(untrack(() => publicTrips));
  let loadingTrips = $state(false);

  const isOwnProfile = untrack(() => user?.id === profile.id);

  onMount(async () => {
    if (trips.length === 0) {
      await loadPublicTrips();
    }
  });

  async function loadPublicTrips() {
    loadingTrips = true;
    try {
      const result = await usersApi.getPublicTrips(profile.id);
      if (result.success) {
        trips = result.data || [];
      }
    } catch (error) {
      console.error("Failed to load trips:", error);
    } finally {
      loadingTrips = false;
    }
  }

  async function toggleFollow() {
    if (!user) {
      router.visit('/login');
      return;
    }

    loading = true;
    try {
      if (isFollowing) {
        await usersApi.unfollow(profile.id);
        isFollowing = false;
        followersCount--;
        addNotification("success", `${profile.name}님을 언팔로우했습니다.`);
      } else {
        await usersApi.follow(profile.id);
        isFollowing = true;
        followersCount++;
        addNotification("success", `${profile.name}님을 팔로우했습니다.`);
      }
    } catch (error: any) {
      addNotification("error", error.response?.data?.errors?.[0]?.message || "오류가 발생했습니다.");
    } finally {
      loading = false;
    }
  }

  function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' });
  }

  function formatTripDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  // 국가 코드 → 이모지 변환
  function getCountryFlag(countryCode: string): string {
    if (!countryCode) return "🌍";
    const codePoints = countryCode
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  }
</script>

<Layout {user}>
  <div class="min-h-screen bg-app">
    <!-- Profile Header -->
    <div class="bg-gradient-to-br from-blue-600 to-cyan-500">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div class="flex flex-col sm:flex-row items-center gap-6">
          <!-- Avatar -->
          <div class="relative">
            {#if profile.avatar_url}
              <img
                src={profile.avatar_url}
                alt={profile.name}
                class="w-28 h-28 sm:w-32 sm:h-32 rounded-full border-4 border-white shadow-xl object-cover"
              />
            {:else}
              <div class="w-28 h-28 sm:w-32 sm:h-32 rounded-full border-4 border-white shadow-xl bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center text-white text-4xl font-bold">
                {profile.name.charAt(0)}
              </div>
            {/if}
          </div>

          <!-- Info -->
          <div class="flex-1 text-center sm:text-left">
            <h1 class="text-2xl sm:text-3xl font-bold text-white">{profile.name}</h1>
            {#if profile.nickname}
              <p class="text-blue-100 text-lg">@{profile.nickname}</p>
            {/if}
            {#if profile.bio}
              <p class="text-white/90 mt-2 max-w-lg">{profile.bio}</p>
            {/if}
            <p class="text-blue-200 text-sm mt-2">
              {formatDate(profile.created_at)}에 가입
            </p>
          </div>

          <!-- Actions -->
          <div class="flex flex-col gap-2">
            {#if isOwnProfile}
              <Link href="/profile/edit" class="px-6 py-2.5 bg-white text-blue-600 rounded-xl font-medium hover:bg-blue-50 transition-colors text-center">
                프로필 수정
              </Link>
            {:else}
              <button
                onclick={toggleFollow}
                disabled={loading}
                class="px-6 py-2.5 rounded-xl font-medium transition-all disabled:opacity-50
                  {isFollowing 
                    ? 'bg-white/20 text-white border-2 border-white hover:bg-white/30' 
                    : 'bg-white text-blue-600 hover:bg-blue-50'}"
              >
                {#if loading}
                  <svg class="animate-spin h-5 w-5 mx-auto" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                {:else}
                  {isFollowing ? '팔로잉' : '팔로우'}
                {/if}
              </button>
            {/if}
          </div>
        </div>

        <!-- Stats -->
        <div class="flex justify-center sm:justify-start gap-8 mt-8">
          <button onclick={() => activeTab = 'trips'} class="text-center hover:opacity-80 transition-opacity">
            <div class="text-2xl font-bold text-white">{profile.stats.trips_count}</div>
            <div class="text-blue-100 text-sm">여행</div>
          </button>
          <button onclick={() => activeTab = 'followers'} class="text-center hover:opacity-80 transition-opacity">
            <div class="text-2xl font-bold text-white">{followersCount}</div>
            <div class="text-blue-100 text-sm">팔로워</div>
          </button>
          <button onclick={() => activeTab = 'following'} class="text-center hover:opacity-80 transition-opacity">
            <div class="text-2xl font-bold text-white">{profile.stats.following_count}</div>
            <div class="text-blue-100 text-sm">팔로잉</div>
          </button>
        </div>
      </div>
    </div>

    <!-- Content Tabs -->
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div class="bg-surface rounded-2xl shadow-sm overflow-hidden">
        <!-- Tab Headers -->
        <div class="flex border-b border">
          <button
            onclick={() => activeTab = 'trips'}
            class="flex-1 py-4 text-center font-medium transition-colors
              {activeTab === 'trips' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-muted hover:text-secondary'}"
          >
            <svg class="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> 다녀온 여행
          </button>
          <button
            onclick={() => activeTab = 'followers'}
            class="flex-1 py-4 text-center font-medium transition-colors
              {activeTab === 'followers' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-muted hover:text-secondary'}"
          >
            팔로워 ({followersCount})
          </button>
          <button
            onclick={() => activeTab = 'following'}
            class="flex-1 py-4 text-center font-medium transition-colors
              {activeTab === 'following' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-muted hover:text-secondary'}"
          >
            팔로잉 ({profile.stats.following_count})
          </button>
        </div>

        <!-- Tab Content -->
        <div class="p-4">
          {#if activeTab === 'trips'}
            {#if loadingTrips}
              <div class="flex justify-center py-12">
                <svg class="animate-spin h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            {:else if trips.length === 0}
              <div class="text-center py-12 text-muted">
                <svg class="w-12 h-12 mx-auto mb-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>공개된 여행이 없습니다</p>
                {#if isOwnProfile}
                  <p class="text-sm mt-1">완료된 여행을 공개로 설정해보세요!</p>
                {/if}
              </div>
            {:else}
              <div class="grid gap-4">
                {#each trips as trip}
                  <Link 
                    href={`/trips/${trip.id}`}
                    class="flex items-center gap-4 p-4 rounded-xl bg-app hover:bg-surface-hover hover:shadow-md transition-all cursor-pointer"
                  >
                    <div class="text-3xl">
                      {getCountryFlag(trip.country_code)}
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-primary">{trip.name}</h3>
                      <p class="text-sm text-secondary">{trip.destination}</p>
                      <p class="text-xs text-muted mt-1">
                        {formatTripDate(trip.start_date)} ~ {formatTripDate(trip.end_date)}
                        <span class="mx-1">•</span>
                        {trip.duration_days}일
                      </p>
                    </div>
                    <div class="flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1 rounded-full text-sm">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      완료
                    </div>
                    <svg class="w-5 h-5 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                {/each}
              </div>
            {/if}
          {:else if activeTab === 'followers'}
            {#if followers.length === 0}
              <div class="text-center py-12 text-muted">
                <svg class="w-12 h-12 mx-auto mb-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p>아직 팔로워가 없습니다</p>
              </div>
            {:else}
              <div class="space-y-3">
                {#each followers as follower}
                  <Link href={`/users/${follower.id}`} class="flex items-center gap-4 p-3 rounded-xl hover:bg-surface-hover transition-colors">
                    {#if follower.avatar_url}
                      <img src={follower.avatar_url} alt={follower.name} class="w-12 h-12 rounded-full object-cover" />
                    {:else}
                      <div class="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center text-white font-medium">
                        {follower.name.charAt(0)}
                      </div>
                    {/if}
                    <div>
                      <div class="font-medium text-primary">{follower.name}</div>
                      {#if follower.nickname}
                        <div class="text-sm text-muted">@{follower.nickname}</div>
                      {/if}
                    </div>
                  </Link>
                {/each}
              </div>
            {/if}
          {:else}
            {#if following.length === 0}
              <div class="text-center py-12 text-muted">
                <svg class="w-12 h-12 mx-auto mb-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p>아직 팔로우하는 사용자가 없습니다</p>
              </div>
            {:else}
              <div class="space-y-3">
                {#each following as followingUser}
                  <Link href={`/users/${followingUser.id}`} class="flex items-center gap-4 p-3 rounded-xl hover:bg-surface-hover transition-colors">
                    {#if followingUser.avatar_url}
                      <img src={followingUser.avatar_url} alt={followingUser.name} class="w-12 h-12 rounded-full object-cover" />
                    {:else}
                      <div class="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center text-white font-medium">
                        {followingUser.name.charAt(0)}
                      </div>
                    {/if}
                    <div>
                      <div class="font-medium text-primary">{followingUser.name}</div>
                      {#if followingUser.nickname}
                        <div class="text-sm text-muted">@{followingUser.nickname}</div>
                      {/if}
                    </div>
                  </Link>
                {/each}
              </div>
            {/if}
          {/if}
        </div>
      </div>
    </div>
  </div>
</Layout>
